import React from "react";
import { JsonLd } from "react-schemaorg";
import { BreadcrumbList } from "schema-dts";
import analytics from "web/script/analytics/analytics";
import { BreadcrumbsSerializer } from "web/types/serializers";
import { Prettify } from "web/types/utils";
import * as styles from "./breadcrumbs.css";

const BREADCRUMB_TYPES = {
    feed: "feed_click",
    pdp: "pdp_related_click",
    the_edit: "the_edit_click",
    curated_collection: "curated_collection_click",
    feature_article: "feature_article_click",
    lyst_news: "lyst_news_click",
};

interface BreadCrumbsProps extends BreadcrumbsSerializer {
    type: keyof typeof BREADCRUMB_TYPES;
    /**
     * Emit the BreadcrumbList JSON-LD alongside the visible trail. Defaults to true.
     * Set false where the server already owns the page's structured data — currently only
     * the Feature Article page, whose graph is built in
     * src/ssaw/portal/template_serializers/modules/json_ld/feature_article_schema.py.
     */
    withJsonLd?: boolean;
}
type BreadCrumbsPropsPretty = Prettify<BreadCrumbsProps>;

export function Breadcrumbs({
    breadcrumbs,
    type,
    withJsonLd = true,
}: BreadCrumbsPropsPretty): React.ReactElement {
    function handleBreadcrumbClick(): void {
        const action = BREADCRUMB_TYPES[type] || "";
        analytics.event(`${type}_breadcrumb`, action, "", false, {}, "", {}, [], false, false);
    }

    return (
        <div data-testid="breadcrumbs">
            <ol className={styles.breadcrumbs}>
                {breadcrumbs.map((breadcrumb) => (
                    <li key={breadcrumb.name} className={styles.breadcrumb}>
                        {breadcrumb.url ? (
                            <a
                                href={breadcrumb.url}
                                onClick={handleBreadcrumbClick}
                                className={styles.link}
                            >
                                <span className={styles.name}>{breadcrumb.name}</span>
                            </a>
                        ) : (
                            <span className={styles.name}>{breadcrumb.name}</span>
                        )}
                    </li>
                ))}
            </ol>
            {withJsonLd && (
                <JsonLd<BreadcrumbList>
                    item={{
                        "@context": "https://schema.org",
                        "@type": "BreadcrumbList",
                        itemListElement: breadcrumbs.map((breadcrumb, i) => ({
                            "@type": "ListItem",
                            position: i + 1,
                            item: {
                                "@id": breadcrumb.url,
                                name: breadcrumb.name,
                            },
                        })),
                    }}
                />
            )}
        </div>
    );
}
